import { useEffect, useRef, useState, useCallback } from 'react';
import { subscribeToOrderUpserts } from '../services/firebase';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { useAuth } from './useAuth';

export type OrderStatus = 'created' | 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled';

export interface OrderItem {
  id?: string;
  name: string;
  quantity: number;
  specialInstructions?: string;
  price?: number;
  size?: string;
}

export interface RealtimeOrder {
  id: string;
  tableNumber: number;
  items: OrderItem[];
  totalAmount: string | number;
  status: OrderStatus;
  createdAt: string | number | Date;
  updatedAt: string | number | Date;
  notes?: string;
  isPaid: boolean;
  paymentMethod: string;
  restaurantId: string;
  restaurantName: string;
  tableId: string;
  username: string;
  waiterId: string;
}

export const useOrdersRealtime = (filterByStatus?: OrderStatus[], initialOrders?: RealtimeOrder[]) => {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();

  const [orders, setOrders] = useState<RealtimeOrder[]>(initialOrders || []);
  const [loading, setLoading] = useState(!initialOrders || initialOrders.length === 0);
  const unsubRef = useRef<() => void>();

  // Función para procesar una nueva orden o actualización
  const processOrderUpdate = useCallback((payload: any) => {
    console.log('🔄 useOrdersRealtime - processOrderUpdate llamado con payload:', payload);
    console.log('🔄 useOrdersRealtime - Tipo de payload:', typeof payload);
    console.log('🔄 useOrdersRealtime - Payload es null/undefined:', payload == null);
    
    if (!payload) {
      console.log('🔄 useOrdersRealtime - Payload vacío, saliendo');
      return;
    }
    
    // Filtrar por restaurante si viene en el payload
    console.log('🔄 useOrdersRealtime - Verificando filtro de restaurante');
    console.log('🔄 useOrdersRealtime - selectedRestaurantId:', selectedRestaurantId);
    console.log('🔄 useOrdersRealtime - payload.restaurantId:', payload?.restaurantId);
    
    if (selectedRestaurantId && payload?.restaurantId && payload.restaurantId !== selectedRestaurantId) {
      console.log('🔄 useOrdersRealtime - Orden filtrada por restaurante:', payload.restaurantId, '!==', selectedRestaurantId);
      return;
    }
    
    console.log('🔄 useOrdersRealtime - Orden pasa el filtro de restaurante, procesando...');

    // Convertir el payload al formato de orden
    console.log('🔄 useOrdersRealtime - Convirtiendo payload a RealtimeOrder...');
    console.log('🔄 useOrdersRealtime - payload.id:', payload.id);
    console.log('🔄 useOrdersRealtime - payload.status:', payload.status);
    console.log('🔄 useOrdersRealtime - payload.items:', payload.items);
    
    const safeNumber = (val: any, def = 0) => {
      const n = typeof val === 'string' ? parseFloat(val) : val;
      return Number.isFinite(n) ? n : def;
    };

    const toDate = (val: any) => {
      if (!val) return new Date();
      if (val instanceof Date) return val;

      // Firestore Timestamp (toDate) o shape {seconds,nanoseconds} / {_seconds,_nanoseconds}
      if (typeof val === 'object') {
        try {
          if (typeof (val as any).toDate === 'function') {
            const d = (val as any).toDate();
            if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
          }
        } catch {
          // noop
        }

        const seconds = (val as any).seconds ?? (val as any)._seconds;
        const nanos = (val as any).nanoseconds ?? (val as any)._nanoseconds;
        if (typeof seconds === 'number') {
          const ms = seconds * 1000 + (typeof nanos === 'number' ? Math.floor(nanos / 1e6) : 0);
          const d = new Date(ms);
          if (!Number.isNaN(d.getTime())) return d;
        }
      }

      // epoch ms o epoch seconds
      if (typeof val === 'number') {
        const ms = val < 1e12 ? val * 1000 : val;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? new Date() : d;
      }

      if (typeof val === 'string') {
        const t = Date.parse(val);
        if (Number.isFinite(t)) return new Date(t);
      }

      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? new Date() : d;
    };

    const normalizeItem = (it: any) => ({
      id: it?.id ?? it?.orderItemId ?? it?.menu_item_id ?? it?.menuItemId ?? undefined,
      name: it?.name ?? it?.menuItemName ?? it?.menu_item_name ?? it?.menuItem?.name ?? 'Item',
      quantity: safeNumber(it?.quantity ?? it?.qty, 1),
      specialInstructions:
        it?.specialInstructions ??
        it?.special_instructions ??
        it?.notes ??
        it?.note ??
        it?.observations ??
        '',
      price: safeNumber(it?.price ?? it?.unit_price ?? it?.unitPrice, 0),
      size: it?.size ?? it?.variant ?? it?.tamano ?? it?.tamaño ?? it?.sizeName ?? it?.size_name,
    });

    // Normalizar items primero para poder calcular total si no viene
    const normalizedItems: OrderItem[] = Array.isArray(payload.items) ? payload.items.map(normalizeItem) : [];
    const computedTotal = normalizedItems.reduce((sum: number, it: OrderItem) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

    const order: RealtimeOrder = {
      id: String(payload.id ?? payload.orderId ?? payload.order_id ?? Math.random()),
      tableNumber: safeNumber(payload.tableNumber ?? payload.table_number ?? payload.table?.number, 0),
      items: normalizedItems,
      totalAmount: (() => {
        const t = safeNumber(payload.totalAmount ?? payload.total_amount ?? payload.total, NaN);
        return Number.isFinite(t) && t > 0 ? t : computedTotal;
      })(),
      status: String(payload.status || 'pending').toLowerCase() as OrderStatus,
      createdAt: toDate(payload.createdAt ?? payload.created_at),
      updatedAt: toDate(payload.updatedAt ?? payload.updated_at),
      notes: payload.notes ?? payload.specialNotes ?? payload.orderNotes ?? '',
      isPaid: Boolean(payload.isPaid ?? payload.is_paid ?? false),
      paymentMethod: payload.paymentMethod ?? payload.payment_method ?? '',
      restaurantId: String(payload.restaurantId ?? payload.restaurant_id ?? ''),
      restaurantName: payload.restaurantName ?? payload.restaurant_name ?? '',
      tableId: String(payload.tableId ?? payload.table_id ?? ''),
      username: payload.username ?? payload.userName ?? payload.waiter_name ?? '',
      waiterId: String(payload.waiterId ?? payload.waiter_id ?? ''),
    };
    
    console.log('🔄 useOrdersRealtime - Orden convertida:', order);

    // Actualizar el array de órdenes
    console.log('🔄 useOrdersRealtime - Actualizando estado de órdenes...');
    
    setOrders(prevOrders => {
      console.log('🔄 useOrdersRealtime - Órdenes previas:', prevOrders);
      
      // Buscar si la orden ya existe
      const existingOrderIndex = prevOrders.findIndex(o => o.id === order.id);
      console.log('🔄 useOrdersRealtime - Índice de orden existente:', existingOrderIndex);
      
      if (existingOrderIndex >= 0) {
        // Si existe, actualizar la orden existente
        console.log('🔄 useOrdersRealtime - Actualizando orden existente:', order.id);
        const updatedOrders = [...prevOrders];
        const prev = updatedOrders[existingOrderIndex];

        // Muchos upserts realtime vienen "parciales" (solo status, etc.).
        // Para no perder datos (notes, items, createdAt), hacemos merge conservador.
        const hasItemsField = Object.prototype.hasOwnProperty.call(payload || {}, 'items');
        const hasCreatedAtField =
          Object.prototype.hasOwnProperty.call(payload || {}, 'createdAt') ||
          Object.prototype.hasOwnProperty.call(payload || {}, 'created_at');
        const hasUpdatedAtField =
          Object.prototype.hasOwnProperty.call(payload || {}, 'updatedAt') ||
          Object.prototype.hasOwnProperty.call(payload || {}, 'updated_at');
        const hasNotesField =
          Object.prototype.hasOwnProperty.call(payload || {}, 'notes') ||
          Object.prototype.hasOwnProperty.call(payload || {}, 'specialNotes') ||
          Object.prototype.hasOwnProperty.call(payload || {}, 'orderNotes');

        updatedOrders[existingOrderIndex] = {
          ...prev,
          ...order,
          items: hasItemsField ? order.items : (prev.items || []),
          createdAt: hasCreatedAtField ? order.createdAt : (prev.createdAt || order.createdAt),
          updatedAt: hasUpdatedAtField ? order.updatedAt : (prev.updatedAt || order.updatedAt),
          notes: hasNotesField ? (order.notes || '') : (prev.notes || order.notes || ''),
          restaurantId: order.restaurantId || prev.restaurantId,
          tableId: order.tableId || prev.tableId,
          username: order.username || prev.username,
          restaurantName: order.restaurantName || prev.restaurantName,
          paymentMethod: order.paymentMethod || prev.paymentMethod,
          waiterId: order.waiterId || prev.waiterId,
          isPaid: order.isPaid ?? prev.isPaid,
          totalAmount: (order.totalAmount !== undefined && order.totalAmount !== null) ? order.totalAmount : prev.totalAmount,
          tableNumber: (order.tableNumber !== undefined && order.tableNumber !== null) ? order.tableNumber : prev.tableNumber,
          status: order.status || prev.status,
        };
        console.log('🔄 useOrdersRealtime - Órdenes después de actualizar:', updatedOrders);
        return updatedOrders;
      } else {
        // Si no existe, agregar la nueva orden al inicio
        console.log('🔄 useOrdersRealtime - Agregando nueva orden:', order.id);
        const newOrders = [order, ...prevOrders];
        console.log('🔄 useOrdersRealtime - Órdenes después de agregar:', newOrders);
        return newOrders;
      }
    });
  }, [selectedRestaurantId]);

  useEffect(() => {
    console.log('🔄 useOrdersRealtime - useEffect ejecutándose');
    console.log('🔄 useOrdersRealtime - user?.uid:', user?.uid);
    console.log('🔄 useOrdersRealtime - selectedRestaurantId:', selectedRestaurantId);
    console.log('🔄 useOrdersRealtime - initialOrders:', initialOrders);
    
    // Limpiar suscripción previa
    if (unsubRef.current) {
      console.log('🔄 useOrdersRealtime - Limpiando suscripción previa');
      unsubRef.current();
      unsubRef.current = undefined;
    }
    
    // Solo limpiar órdenes si no hay órdenes iniciales
    if (!initialOrders || initialOrders.length === 0) {
      console.log('🔄 useOrdersRealtime - No hay órdenes iniciales, limpiando estado');
      setOrders([]);
      setLoading(true);
    } else {
      console.log('🔄 useOrdersRealtime - Hay órdenes iniciales, manteniendo estado');
    }

    if (!user?.uid) {
      console.log('🔄 useOrdersRealtime - No hay usuario, saliendo');
      setLoading(false);
      return;
    }

    console.log('🔄 useOrdersRealtime - Suscribiendo a órdenes en tiempo real para usuario:', user.uid, 'restaurante:', selectedRestaurantId);

    // Suscribir al nodo de upsert del usuario autenticado
    try {
      console.log('🔄 useOrdersRealtime - Llamando a subscribeToOrderUpserts...');
      unsubRef.current = subscribeToOrderUpserts(user.uid, processOrderUpdate);
      console.log('🔄 useOrdersRealtime - Suscripción exitosa, unsubRef.current:', !!unsubRef.current);
    } catch (error) {
      console.error('🔄 useOrdersRealtime - Error en suscripción:', error);
    }

    // Marcar como no cargando después de un breve delay para permitir que lleguen las primeras órdenes
    const loadingTimer = setTimeout(() => {
      console.log('🔄 useOrdersRealtime - Timer de loading completado');
      setLoading(false);
    }, 1000);

    return () => {
      console.log('🔄 useOrdersRealtime - Cleanup del useEffect');
      if (unsubRef.current) {
        console.log('🔄 useOrdersRealtime - Ejecutando cleanup de suscripción');
        unsubRef.current();
        unsubRef.current = undefined;
      }
      clearTimeout(loadingTimer);
    };
  }, [user?.uid, selectedRestaurantId, processOrderUpdate, initialOrders]);

  // Filtrar órdenes por estado si se especifica
  const filteredOrders = filterByStatus 
    ? orders.filter(order => filterByStatus.includes(order.status))
    : orders;

  // Agrupar órdenes por estado
  const ordersByStatus = {
    created: orders.filter(order => order.status === 'created'),
    pending: orders.filter(order => order.status === 'pending'),
    preparing: orders.filter(order => order.status === 'preparing'),
    ready: orders.filter(order => order.status === 'ready'),
    delivered: orders.filter(order => order.status === 'delivered'),
    cancelled: orders.filter(order => order.status === 'cancelled'),
  };

  console.log('🔄 useOrdersRealtime - Estado actual del hook:');
  console.log('🔄 useOrdersRealtime - orders (sin filtrar):', orders);
  console.log('🔄 useOrdersRealtime - filteredOrders:', filteredOrders);
  console.log('🔄 useOrdersRealtime - ordersByStatus:', ordersByStatus);
  console.log('🔄 useOrdersRealtime - loading:', loading);
  console.log('🔄 useOrdersRealtime - filterByStatus:', filterByStatus);

  return {
    orders: filteredOrders,
    ordersByStatus,
    loading,
    // Función para forzar actualización manual
    refresh: () => {
      console.log('🔄 useOrdersRealtime - Función refresh llamada');
      setOrders([]);
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    },
    // Función para actualizar órdenes iniciales
    setInitialOrders: (newOrders: RealtimeOrder[]) => {
      console.log('🔄 useOrdersRealtime - setInitialOrders llamado con:', newOrders);
      setOrders(newOrders);
      setLoading(false);
    }
  };
};
